import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import { CommanServices } from "@/services/commanServices";

import { Activity, RefreshCw, CheckCircle, XCircle, Clock, Users, Send, History, Radio, Cpu } from "lucide-react";

import { useDutyCheckJob, COMPLETE_EVENT } from "@/context/DutyCheckJobContext";

import DutyCheckHistoryModal from "@/components/DutyCheckHistoryModal";
import { formatDateTime } from "@/uitilites/utility";



const POLL_ACTIVE_MS = 15000;

const TABLE_MAX_HEIGHT = "min(480px, 60vh)";



const normalizeScopeList = (row) => {

    const raw = row?.scopes;

    if (Array.isArray(raw) && raw.length) {

        return [...new Set(raw.map((s) => String(s).trim()).filter(Boolean))];

    }

    if (row?.affiliation) {

        return row.affiliation

            .split(/[·,]/)

            .map((s) => s.trim())

            .filter(Boolean);

    }

    return [];

};



const ScopeChips = ({ scopes }) => {

    const items = scopes?.length ? scopes : ["—"];

    const label = items.join(", ");



    return (

        <div className="flex flex-wrap gap-1 max-w-[280px]" title={label}>

            {items.map((scope) => (

                <span

                    key={scope}

                    className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium border border-slate-200 leading-tight"

                >

                    {scope}

                </span>

            ))}

        </div>

    );

};



const normalizeResponsesPayload = (data) => {

    if (!data) return { rows: [], stats: null };

    if (Array.isArray(data)) {

        return {

            rows: data,

            stats: {

                total: data.length,

                pending: data.filter((r) => r.status === "pending").length,

                responded: data.filter((r) => r.status !== "pending").length,

                in_duty: data.filter((r) => r.status === "in_duty").length,

                not_in_duty: data.filter((r) => r.status === "not_in_duty").length,

            },

        };

    }

    return {

        rows: data.rows || [],

        stats: data.stats || null,

    };

};



const StatusPage = () => {

    const [liveCheck, setLiveCheck] = useState(null);

    const [displayCheck, setDisplayCheck] = useState(null);

    const [viewMode, setViewMode] = useState("live");

    const [responses, setResponses] = useState([]);

    const [stats, setStats] = useState(null);

    const [loading, setLoading] = useState(false);

    const [detailLoading, setDetailLoading] = useState(false);

    const [filter, setFilter] = useState("all");

    const [historyOpen, setHistoryOpen] = useState(false);

    const { openModal, isRunning } = useDutyCheckJob();



    const responsesCache = useRef(new Map());

    const liveCheckIdRef = useRef(null);



    const applyCheckData = useCallback((check, rows, apiStats) => {

        setDisplayCheck(check);

        setResponses(rows);

        setStats(apiStats);

    }, []);



    const fetchResponsesForCheck = useCallback(async (checkId, checkMeta, useCache = true) => {

        if (useCache && responsesCache.current.has(checkId)) {

            const cached = responsesCache.current.get(checkId);

            applyCheckData(checkMeta, cached.rows, cached.stats);

            return;

        }



        setDetailLoading(true);

        try {

            const respRes = await CommanServices.getDutyCheckResponses(checkId);

            if (respRes?.status) {

                const { rows, stats: apiStats } = normalizeResponsesPayload(respRes.data);

                responsesCache.current.set(checkId, { rows, stats: apiStats });

                applyCheckData(checkMeta, rows, apiStats);

            }

        } catch (e) {

            console.error(e);

        } finally {

            setDetailLoading(false);

        }

    }, [applyCheckData]);



    const fetchLive = useCallback(async (silent = false) => {

        if (!silent) setLoading(true);

        try {

            const checkRes = await CommanServices.getLatestDutyCheck();

            if (checkRes?.status && checkRes?.data?.id) {

                const check = checkRes.data;

                setLiveCheck(check);

                liveCheckIdRef.current = check.id;



                if (viewMode === "live") {

                    await fetchResponsesForCheck(check.id, check, true);

                }

            } else {

                setLiveCheck(null);

                liveCheckIdRef.current = null;

                if (viewMode === "live") {

                    setDisplayCheck(null);

                    setResponses([]);

                    setStats(null);

                }

            }

        } catch (e) {

            console.error(e);

        } finally {

            if (!silent) setLoading(false);

        }

    }, [viewMode, fetchResponsesForCheck]);



    useEffect(() => {

        fetchLive();

    }, [fetchLive]);



    useEffect(() => {

        const onJobComplete = () => {

            responsesCache.current.clear();

            setViewMode("live");

            fetchLive();

        };

        window.addEventListener(COMPLETE_EVENT, onJobComplete);

        return () => window.removeEventListener(COMPLETE_EVENT, onJobComplete);

    }, [fetchLive]);



    useEffect(() => {

        if (viewMode !== "live" || !liveCheck?.id) return undefined;

        const interval = setInterval(() => fetchLive(true), POLL_ACTIVE_MS);

        return () => clearInterval(interval);

    }, [viewMode, liveCheck?.id, fetchLive]);



    const handleSelectHistory = useCallback(

        async (item) => {

            if (!item?.id || item.kind === "scheduled") return;

            const isLatest =
                item.id === liveCheck?.id || item.id === liveCheckIdRef.current;
            setViewMode(isLatest ? "live" : "history");

            setFilter("all");

            const meta = {

                id: item.id,

                created_at: item.created_at,

                sent_by_name: item.sent_by_name,

                target_type: item.target_type,

                target_count: item.target_count,

            };

            await fetchResponsesForCheck(item.id, meta, true);

        },

        [fetchResponsesForCheck, liveCheck?.id]

    );



    const backToLive = useCallback(async () => {

        setViewMode("live");

        setFilter("all");

        if (liveCheck?.id) {

            await fetchResponsesForCheck(liveCheck.id, liveCheck, true);

        } else {

            await fetchLive();

        }

    }, [liveCheck, fetchResponsesForCheck, fetchLive]);



    const handleRefresh = useCallback(() => {

        if (viewMode === "live") {

            if (displayCheck?.id) {

                responsesCache.current.delete(displayCheck.id);

            }

            fetchLive();

        } else if (displayCheck?.id) {

            responsesCache.current.delete(displayCheck.id);

            fetchResponsesForCheck(displayCheck.id, displayCheck, false);

        }

    }, [viewMode, displayCheck, fetchLive, fetchResponsesForCheck]);



    const computedStats = useMemo(() => {

        if (stats) {

            return {

                total: stats.total ?? 0,

                pending: stats.pending ?? 0,

                in_duty: stats.in_duty ?? 0,

                not_in_duty: stats.not_in_duty ?? 0,

                responded: stats.responded ?? (stats.in_duty ?? 0) + (stats.not_in_duty ?? 0),

            };

        }

        const pending = responses.filter((r) => r.status === "pending").length;

        const inDuty = responses.filter((r) => r.status === "in_duty").length;

        const notInDuty = responses.filter((r) => r.status === "not_in_duty").length;

        return {

            total: responses.length,

            pending,

            in_duty: inDuty,

            not_in_duty: notInDuty,

            responded: inDuty + notInDuty,

        };

    }, [stats, responses]);



    const filteredResponses = useMemo(() => {

        if (filter === "all") return responses;

        return responses.filter((r) => r.status === filter);

    }, [responses, filter]);



    const renderStatusBadge = (status) => {

        if (status === "in_duty") {

            return (

                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200">

                    <CheckCircle className="w-3.5 h-3.5" />

                    In Duty

                </span>

            );

        }

        if (status === "not_in_duty") {

            return (

                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200">

                    <XCircle className="w-3.5 h-3.5" />

                    Not In Duty

                </span>

            );

        }

        return (

            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-200">

                <Clock className="w-3.5 h-3.5" />

                Pending

            </span>

        );

    };



    const isHistoricalView =
        viewMode === "history" &&
        displayCheck?.id &&
        liveCheck?.id &&
        displayCheck.id !== liveCheck.id;

    const showEmptyNoCheck = !displayCheck && !loading && !isRunning && !detailLoading;

    const showTableLoading = (loading || detailLoading) && responses.length === 0;



    return (

        <div className="flex-1 min-h-0 overflow-y-auto w-full bg-gray-50 text-gray-800 p-6">

            <DutyCheckHistoryModal

                isOpen={historyOpen}

                onClose={() => setHistoryOpen(false)}

                onSelectItem={handleSelectHistory}

                selectedId={isHistoricalView ? displayCheck?.id : null}

                liveCheckId={liveCheck?.id}

            />



            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">

                <div className="flex items-center gap-3">

                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">

                        <Activity className="w-5 h-5 text-white" />

                    </div>

                    <div>

                        <h1 className="text-2xl font-bold text-gray-900">Duty Status Check</h1>

                        <p className="text-gray-500 text-sm">

                            {displayCheck

                                ? `${isHistoricalView ? "Viewing history" : "Live"} · Sent ${formatDateTime(displayCheck.created_at)}`

                                : "No duty check sent yet"}

                        </p>

                    </div>

                </div>



                <div className="flex items-center gap-2 flex-wrap">

                    {isHistoricalView && (

                        <button

                            type="button"

                            onClick={backToLive}

                            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-sm font-semibold border border-indigo-200"

                        >

                            <Radio className="w-4 h-4" />

                            Back to Live

                        </button>

                    )}

                    <button

                        type="button"

                        onClick={() => setHistoryOpen(true)}

                        className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-gray-600 rounded-xl text-sm border border-gray-200 shadow-sm"

                    >

                        <History className="w-4 h-4" />

                        History

                    </button>

                    <button

                        onClick={handleRefresh}

                        disabled={loading || detailLoading}

                        className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-gray-600 rounded-xl text-sm border border-gray-200 shadow-sm"

                    >

                        <RefreshCw className={`w-4 h-4 ${loading || detailLoading ? "animate-spin" : ""}`} />

                        Refresh

                    </button>

                    <button

                        onClick={openModal}

                        disabled={isRunning}

                        className="flex items-center gap-2 bg-[#0a1a3f] hover:bg-[#1c2e5a] text-white px-6 py-3 rounded-2xl shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50"

                    >

                        {isRunning ? (

                            <RefreshCw className="w-4 h-4 animate-spin" />

                        ) : (

                            <Send className="w-4 h-4" />

                        )}

                        <span className="font-bold text-sm">Send Status Check</span>

                    </button>

                </div>

            </div>



            {isHistoricalView && (

                <div className="mb-4 px-4 py-2.5 rounded-xl bg-indigo-50 border border-indigo-100 text-sm text-indigo-800">

                    Viewing historical status check · data does not auto-refresh

                </div>

            )}



            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">

                {[

                    { label: "Total Targeted", value: computedStats.total, icon: <Users className="w-5 h-5" />, color: "bg-indigo-50 border-indigo-200 text-indigo-600" },

                    { label: "In Duty", value: computedStats.in_duty, icon: <CheckCircle className="w-5 h-5" />, color: "bg-emerald-50 border-emerald-200 text-emerald-600" },

                    { label: "Not In Duty", value: computedStats.not_in_duty, icon: <XCircle className="w-5 h-5" />, color: "bg-red-50 border-red-200 text-red-600" },

                    { label: "Pending", value: computedStats.pending, icon: <Clock className="w-5 h-5" />, color: "bg-amber-50 border-amber-200 text-amber-600" },

                ].map((card) => (

                    <div key={card.label} className={`rounded-2xl border p-5 flex items-center gap-4 bg-white ${card.color}`}>

                        <div className="opacity-80">{card.icon}</div>

                        <div>

                            <p className="text-2xl font-bold text-gray-900">{card.value}</p>

                            <p className="text-xs mt-0.5 text-gray-500">{card.label}</p>

                        </div>

                    </div>

                ))}

            </div>



            <div className="flex gap-2 mb-4 flex-wrap">

                {[

                    { key: "all", label: "All" },

                    { key: "pending", label: "Pending" },

                    { key: "in_duty", label: "In Duty" },

                    { key: "not_in_duty", label: "Not In Duty" },

                ].map((tab) => (

                    <button

                        key={tab.key}

                        onClick={() => setFilter(tab.key)}

                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${filter === tab.key

                            ? "bg-blue-600 text-white shadow-sm"

                            : "bg-white text-gray-500 hover:bg-gray-100 border border-gray-200"

                            }`}

                    >

                        {tab.label}

                    </button>

                ))}

            </div>



            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">

                {showEmptyNoCheck ? (

                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">

                        <Activity className="w-12 h-12 mb-4 opacity-30" />

                        <p className="text-lg font-medium text-gray-500">No duty check sent yet</p>

                        <p className="text-sm mt-1 text-gray-400">Click &quot;Send Status Check&quot; or open History.</p>

                    </div>

                ) : showTableLoading ? (

                    <div className="flex items-center justify-center py-20 text-gray-400">

                        <RefreshCw className="w-6 h-6 animate-spin mr-3" />

                        Loading tracking data…

                    </div>

                ) : filteredResponses.length === 0 ? (

                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">

                        <Clock className="w-10 h-10 mb-3 opacity-30" />

                        <p className="text-gray-500">No users match this filter.</p>

                    </div>

                ) : (

                    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: TABLE_MAX_HEIGHT }}>

                        <table className="w-full min-w-[720px]">

                            <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(243,244,246)]">

                                <tr className="border-b border-gray-100 text-left text-xs text-gray-400 uppercase tracking-wider">

                                    <th className="px-6 py-4 bg-gray-50">Name</th>

                                    <th className="px-6 py-4 bg-gray-50">Scope</th>

                                    <th className="px-6 py-4 bg-gray-50">Status</th>

                                    <th className="px-6 py-4 bg-gray-50 whitespace-nowrap">Sent At</th>

                                    <th className="px-6 py-4 bg-gray-50 whitespace-nowrap">Responded At</th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredResponses.map((r) => (

                                    <tr

                                        key={r.id || `${r.user_id}-${r.sent_at}`}

                                        className="border-b border-gray-50 hover:bg-gray-50 transition-colors"

                                    >

                                        <td className="px-6 py-4 align-top">

                                            <div className="flex items-center gap-3">

                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm shrink-0">

                                                    {(r.user_name || "?").charAt(0).toUpperCase()}

                                                </div>

                                                <span className="font-medium text-gray-800">{r.user_name || "Unknown"}</span>

                                            </div>

                                        </td>

                                        <td className="px-6 py-4 align-top">

                                            <ScopeChips scopes={normalizeScopeList(r)} />

                                        </td>

                                        <td className="px-6 py-4 align-top whitespace-nowrap">{renderStatusBadge(r.status)}</td>

                                        <td className="px-6 py-4 text-gray-400 text-sm whitespace-nowrap align-top">{formatDateTime(r.sent_at)}</td>

                                        <td className="px-6 py-4 text-gray-400 text-sm whitespace-nowrap align-top">{formatDateTime(r.responded_at)}</td>

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>



            {displayCheck && (

                <p className="text-center text-xs text-gray-400 mt-4">

                    Check ID: {displayCheck.id} · Sent by {displayCheck.sent_by_name}

                    {viewMode === "live" ? ` · ${computedStats.responded} responded · Live refresh 15s` : " · Historical view"}

                </p>

            )}

            {/* IoT device checks — placeholder, module to be implemented later */}
            <div className="mt-10" data-testid="iot-check-section">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-600 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">IoT Device Checks</h2>
                        <p className="text-gray-500 text-sm">Connectivity and health checks for registered IoT devices</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-12">
                    <Cpu className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-gray-500">IoT device checks coming soon</p>
                    <p className="text-xs text-gray-400 mt-1">Once devices are registered in the IoT Devices module, their status checks will appear here.</p>
                </div>
            </div>

        </div>

    );

};



export default StatusPage;


